import React, { FunctionComponent, useMemo } from "react";

import { DATE_FORMATS, DateTime } from "@bps/date-time";
import {
  IColumn,
  Link,
  ScrollablePane,
  Stack,
  TooltipHost
} from "@bps/fluent-ui";
import { ScrollListener } from "@components/tables/ScrollListener";
import { ShimmeredDetailsList } from "@components/tables/ShimmeredDetailsList";
import {
  BpIdAuditLogArgs,
  BpIdAuditLogDto
} from "@libs/api/gateways/bp-id/bp-id.dtos";
import { useBpIdAuditLogs } from "@libs/api/gateways/bp-id/bp-id.hooks";
import { config } from "@libs/config/config";
import { getPagesResult } from "@libs/paging/paging.utils";

import { TenantLink } from "./TenantLink";

interface BpIdAuditLogListProps {
  bpIdUserId: string;
  filters: BpIdAuditLogArgs;
}
export const BpIdAuditLogList: FunctionComponent<BpIdAuditLogListProps> = ({
  bpIdUserId,
  filters
}) => {
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage
  } = useBpIdAuditLogs(bpIdUserId, filters);

  const items = useMemo(() => getPagesResult(data), [data]);

  const columns: IColumn[] = [
    {
      key: "timestamp",
      name: "On",
      maxWidth: 180,
      minWidth: 100,
      onRender: (item: BpIdAuditLogDto) =>
        item.timeStamp
          ? DateTime.fromISO(item.timeStamp).toFormat(
              DATE_FORMATS.LONG_DATE_TIME_FORMAT
            )
          : "-"
    },
    {
      key: "tenant",
      name: "Tenant",
      maxWidth: 100,
      minWidth: 100,
      onRender: (item: BpIdAuditLogDto) => (
        <TenantLink tenantId={item.tenantId} />
      )
    },
    {
      key: "severity",
      name: "Severity",
      maxWidth: 100,
      minWidth: 100,
      onRender: (item: BpIdAuditLogDto) => (
        <TooltipHost content={item.severity}>{item.severity}</TooltipHost>
      )
    },
    {
      key: "eventType",
      name: "Event type",
      maxWidth: 100,
      minWidth: 100,
      onRender: (item: BpIdAuditLogDto) => item.eventType
    },
    {
      key: "aventAction",
      name: "Event Action",
      maxWidth: 170,
      minWidth: 140,
      onRender: (item: BpIdAuditLogDto) => item.eventAction
    },
    {
      key: "ipAddress",
      name: "IP Address",
      maxWidth: 300,
      minWidth: 10,
      onRender: (item: BpIdAuditLogDto) => item.ipAddress
    },
    {
      key: "errorCode",
      name: "Error Code",
      maxWidth: 100,
      minWidth: 80,
      onRender: (item: BpIdAuditLogDto) =>
        `${item.errorCode ?? ""} ${item.errorMessage ?? ""}`
    },
    {
      key: "b2cCorrelationId",
      name: "B2C Correlation Id",
      maxWidth: 100,
      minWidth: 80,
      onRender: (item: BpIdAuditLogDto) => {
        if (item.timeStamp && item.b2cCorrelationId) {
          const from = DateTime.fromISO(item.timeStamp)
            .setZone("utc")
            ?.minus({ minute: 2 })
            ?.toISO();

          const to = DateTime.fromISO(item.timeStamp)
            .setZone("utc")
            ?.plus({ minute: 2 })
            ?.toISO();
          return (
            <Link
              href={`${config.seqUrl}?filter=B2CCorrelationId%20%3D%20'${item.b2cCorrelationId}'&from=${from}&to=${to}`}
              target="_blank"
              rel="noreferrer"
            >
              {item.b2cCorrelationId}
            </Link>
          );
        }
      }
    },
    {
      key: "correlationId",
      name: " Correlation Id",
      maxWidth: 100,
      minWidth: 80,
      onRender: (item: BpIdAuditLogDto) => {
        if (item.timeStamp && item.correlationId) {
          const from = DateTime.fromISO(item.timeStamp)
            .setZone("utc")
            ?.minus({ minute: 2 })
            ?.toISO();

          const to = DateTime.fromISO(item.timeStamp)
            .setZone("utc")
            ?.plus({ minute: 2 })
            ?.toISO();

          const split = item.correlationId.split(":");
          const correlationId = split.length > 0 ? split[0] : "";
          const correctedCorrelationId = correlationId.replace("+", "%2B");
          return (
            <Link
              href={`${config.seqUrl}?filter=BpTraceRootId%20%3D%20'${correctedCorrelationId}'&from=${from}&to=${to}`}
              target="_blank"
              rel="noreferrer"
            >
              {item.correlationId}
            </Link>
          );
        }
      }
    }
  ];

  const handleScrolledToBottom = async () => {
    if (hasNextPage) {
      await fetchNextPage();
    }
  };

  return (
    <Stack styles={{ root: { position: "relative", height: 800 } }}>
      <ScrollablePane>
        <ShimmeredDetailsList
          items={items}
          enableShimmer={isLoading}
          errorMessage={error?.message}
          columns={columns}
          stickyHeader
        />
        <ScrollListener
          hasNextPage={true}
          onScrolledToBottom={handleScrolledToBottom}
        />
      </ScrollablePane>
    </Stack>
  );
};
